import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { describeError } from '../../../services/errorMessage';
import { generateVorankuendigungPDF } from '../../../services/pdf/generatorVorank';
import { berlinIsoTag } from '../../../services/vorank/vorankAnfragePruefung';
import {
  ladeVorankPublic,
  sendeVorankAnfrage,
  type VorankPublicLaden,
} from '../../../services/vorank/vorankUnterschriftPublicService';
import type {
  VorankPdfUnterschrift,
  VorankRouteAntwort,
  VorankUnterschriftArt,
} from '../../../services/vorank/vorankUnterschriftTypes';
import type { VorankuendigungData } from '../../../types';
import { bereiteUnterschriftBildAuf } from './unterschriftBild';
import {
  ABFRAGE_TAKT_MS,
  NETZ_FOLGE,
  abfrageSchritt,
  abfrageUebernehmen,
  bereinigeName,
  bereinigeText,
  DATUM_FELD_LEER,
  datumFeldFuer,
  datumGrenzen,
  datumSelbstGeaendert,
  folgeNachSenden,
  gleichesDatumFeld,
  geraetAusZeiger,
  kannUnterschreiben,
  korrekturHinweis,
  ladeDaten,
  ladeNachFolge,
  nachgeladen,
  namensVorgabe,
  normalisiereToken,
  pdfUnterschriftAus,
  seitenZustand,
  tokenGueltig,
  vollerName,
  vorankPdfDateiname,
  TEXTE,
  type DatumFeld,
  type NamensTeile,
  type RechnerWeg,
  type SendeFolge,
  type VorankLadeStand,
} from './vorankUnterschriftLogic';

const OHNE_NAMEN: NamensTeile = { vorname: '', nachname: '' };

export type PdfStand =
  | { status: 'keins' }
  | { status: 'bereit'; url: string }
  | { status: 'fehler'; text: string };

/** Die gezeichnete oder hochgeladene Unterschrift, bereit zum Absenden. */
export interface UnterschriftBild {
  daten: string;
  art: VorankUnterschriftArt;
}

const istGrobZeiger = (): boolean => {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
};

/**
 * `ladeVorankPublic` wirft laut Vertrag nie; ein Wurf gilt trotzdem als
 * Ladefehler, damit die Seite nie hängt. Als `.catch` statt `try`: so steht
 * jedes `setState` der Aufrufer hinter einem `await` (Effekt-Regel).
 */
const ladeAbsichern = (token: string): Promise<VorankPublicLaden> =>
  ladeVorankPublic(token).catch((err: unknown): VorankPublicLaden => ({ art: 'fehler', text: describeError(err) }));

/** PNG-data-URL des Unterschriftsfelds → Datei für `bereiteUnterschriftBildAuf`. */
const alsDatei = (dataUrl: string): File => {
  const roh = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return new File([bytes], 'unterschrift.png', { type: 'image/png' });
};

/** PDF im Browser erzeugen (kein Storage-Zugriff) → Blob-Adresse. */
const pdfAdresse = (inhalt: VorankuendigungData, unterschrift: VorankPdfUnterschrift | null): string => {
  const doc = generateVorankuendigungPDF(inhalt, { unterschrift });
  return URL.createObjectURL(doc.output('blob'));
};

const herunterladen = (url: string, dateiname: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = dateiname;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Zustand der öffentlichen Seite `#/vorankuendigung/<token>`
 * (docs/vorankuendigung-unterschrift-plan.md §6, §6.1).
 *
 * Laden über `ladeVorankPublic` (anon-RPC), Unterschreiben und Korrektur
 * über `sendeVorankAnfrage` (Route `api/vorank-unterschrift`). Beide werfen
 * nie; ein Wurf gilt trotzdem als Netzfehler, damit die Seite nie hängt.
 *
 * Am Rechner mit QR fragt eine Uhr alle 5 s nach, ob am Handy unterschrieben
 * wurde — nur bei sichtbarem Tab, höchstens 15 min (Regeln in
 * vorankUnterschriftLogic). Kein `try…finally` (React-Compiler), Zustand nur
 * nach `await` (Effekt-Regel).
 */
export function useVorankUnterschrift(rohToken: string) {
  const token = normalisiereToken(rohToken);
  const gueltig = tokenGueltig(token);

  const [lade, setLade] = useState<VorankLadeStand>(() => (gueltig ? 'laedt' : { art: 'ungueltig' }));
  const [geraet] = useState(() => geraetAusZeiger(istGrobZeiger()));
  /**
   * Heute als Berliner Kalendertag (Obergrenze und Vorgabe des Datums, E6) —
   * neu genommen beim Laden, wenn der Tab wieder sichtbar wird, beim Fokus
   * auf das Datumsfeld und unmittelbar vor dem Absenden: ein Tab kann über
   * Mitternacht offen bleiben.
   */
  const [heute, setHeute] = useState(() => berlinIsoTag(new Date()));

  // Formular — Vor- und Nachname getrennt, an die Route geht `vollerName` (E6).
  const [gelesen, setGelesen] = useState(false);
  const [name, setName] = useState<NamensTeile>(OHNE_NAMEN);
  /** Datum samt eigener Vorbelegung — so ist erkennbar, ob der Nutzer es geändert hat. */
  const [datumFeld, setDatumFeld] = useState<DatumFeld>(DATUM_FELD_LEER);
  const [funktion, setFunktion] = useState('');
  const [bild, setBild] = useState<UnterschriftBild | null>(null);
  const [padOffen, setPadOffen] = useState(false);
  const [weg, setWeg] = useState<RechnerWeg | null>(null);
  const [bildArbeitet, setBildArbeitet] = useState(false);
  const [bildFehler, setBildFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [danke, setDanke] = useState(false);

  // Korrektur
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  const [korrekturName, setKorrekturName] = useState<NamensTeile>(OHNE_NAMEN);
  const [korrekturText, setKorrekturText] = useState('');
  const [korrekturSendet, setKorrekturSendet] = useState(false);
  const [korrekturFehler, setKorrekturFehler] = useState<string | null>(null);
  const [korrekturGemeldet, setKorrekturGemeldet] = useState(false);

  // PDF
  const [pdf, setPdf] = useState<PdfStand>({ status: 'keins' });
  const [pdfSigniert, setPdfSigniert] = useState<PdfStand>({ status: 'keins' });

  // QR am Rechner
  const [qrBild, setQrBild] = useState<string | null>(null);
  const [qrFehler, setQrFehler] = useState<string | null>(null);
  const [abfrageStart, setAbfrageStart] = useState<number | null>(null);
  const [abfrageZeitUm, setAbfrageZeitUm] = useState(false);

  /** Riegel gegen den zweiten Tipp: `sendet` steht erst nach dem nächsten Render. */
  const sendetRef = useRef(false);
  const ladeLauf = useRef(0);
  const montiert = useRef(true);
  const pdfAdressen = useRef<string[]>([]);

  useEffect(() => {
    montiert.current = true;
    const adressen = pdfAdressen.current;
    return () => {
      montiert.current = false;
      // Blob-Adressen beim Verlassen freigeben.
      adressen.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  useEffect(() => {
    document.title = TEXTE.seitenTitel;
  }, []);

  /** Ergebnis eines Ladelaufs übernehmen — nur das des zuletzt gestarteten. */
  const geladen = useCallback((lauf: number, e: VorankPublicLaden) => {
    if (!montiert.current || lauf !== ladeLauf.current) return;
    if (e.art === 'ok') {
      // Vorgabe aus der Fassung nur, solange niemand selbst etwas getippt hat
      // — weder Vor- noch Nachname (sonst mischte sich Getipptes mit der Vorgabe).
      const vorgabe = namensVorgabe(e.daten);
      const leer = (n: NamensTeile) => !n.vorname && !n.nachname;
      setName((bisher) => (leer(bisher) ? vorgabe : bisher));
      setKorrekturName((bisher) => (leer(bisher) ? vorgabe : bisher));
      // Datum: heute in Berlin (nicht vor dem Tag der Fassung). Die Vorbelegung
      // folgt dem Tag; ein selbst geändertes Datum bleibt, solange es gilt.
      const tag = berlinIsoTag(new Date());
      const g = datumGrenzen(e.daten.fassungVom, tag);
      setHeute(tag);
      setDatumFeld((f) => datumFeldFuer(f, g));
    }
    setLade(e);
  }, []);

  /** Fassung laden. Zustand erst im `.then` (Effekt-Regel). */
  const laden = useCallback((): void => {
    if (!gueltig) return;
    const lauf = ++ladeLauf.current;
    void ladeAbsichern(token).then((e) => geladen(lauf, e));
  }, [geladen, gueltig, token]);

  useEffect(() => {
    laden();
  }, [laden]);

  const erneutLaden = useCallback(() => {
    setLade('laedt');
    laden();
  }, [laden]);

  /** Nach dem Absenden: nur ein lesbarer Stand ersetzt den bisherigen. */
  const nachladen = useCallback(async (): Promise<void> => {
    const lauf = ++ladeLauf.current;
    const e = await ladeAbsichern(token);
    if (!montiert.current || lauf !== ladeLauf.current) return;
    setLade((alt) => nachgeladen(alt, e));
  }, [token]);

  /** Grenzen des Datumsfelds: Tag der Fassung bis heute (Berlin) — immer mit dem aktuellen `heute`. */
  const fassungVom = ladeDaten(lade)?.fassungVom ?? null;
  const grenzen = useMemo(() => datumGrenzen(fassungVom, heute), [fassungVom, heute]);

  /**
   * Den aktuellen Berliner Tag übernehmen (Tab über Mitternacht offen): die
   * Grenzen rechnen mit ihm, das Datumsfeld folgt nach `datumFeldFuer`. Vor
   * dem Laden der Fassung bleibt das Feld leer (das übernimmt `geladen`).
   */
  const tagNeuNehmen = useCallback(() => {
    const tag = berlinIsoTag(new Date());
    setHeute(tag);
    if (!fassungVom) return;
    const g = datumGrenzen(fassungVom, tag);
    setDatumFeld((f) => {
      const neu = datumFeldFuer(f, g);
      return gleichesDatumFeld(neu, f) ? f : neu;
    });
  }, [fassungVom]);

  // Zurück im Tab: der Tag kann inzwischen ein anderer sein.
  useEffect(() => {
    const beiSichtwechsel = () => {
      if (document.visibilityState === 'visible') tagNeuNehmen();
    };
    document.addEventListener('visibilitychange', beiSichtwechsel);
    return () => document.removeEventListener('visibilitychange', beiSichtwechsel);
  }, [tagNeuNehmen]);

  /** Folge einer Antwort der Route auf Ladestand und Seite übertragen. */
  const folgeAnwenden = useCallback((folge: SendeFolge, gesendet: { bild: UnterschriftBild; datum: string } | null) => {
    const lokal = folge.danke && gesendet
      ? {
        name: vollerName(name),
        funktion: bereinigeName(funktion),
        bild: gesendet.bild.daten,
        art: gesendet.bild.art,
        am: new Date().toISOString(),
        datum: gesendet.datum,
      }
      : null;
    setLade((alt) => ladeNachFolge(alt, folge, lokal));
    if (folge.danke) setDanke(true);
    if (folge.neuLaden) void nachladen();
  }, [funktion, name, nachladen]);

  // ── Unterschrift ────────────────────────────────────────────────────────

  /**
   * Gezeichnete Unterschrift übernehmen — auf den Inhalt zugeschnitten wie ein
   * hochgeladenes Bild, sonst stünde sie mit dem leeren Rand des Felds klein
   * im 55 × 14 mm-Feld der PDF. Scheitert der Zuschnitt, gilt die Zeichnung
   * unverändert (eine Unterschrift zu verlieren wäre schlimmer).
   */
  const unterschriftUebernehmen = useCallback(async (dataUrl: string): Promise<void> => {
    setPadOffen(false);
    setFehler(null);
    setBildArbeitet(true);
    let zugeschnitten: string | null = null;
    try {
      zugeschnitten = await bereiteUnterschriftBildAuf(alsDatei(dataUrl));
    } catch {
      zugeschnitten = null;
    }
    if (!montiert.current) return;
    setBildArbeitet(false);
    setBild({ daten: zugeschnitten ?? dataUrl, art: 'gezeichnet' });
  }, []);

  const unterschriftLoeschen = useCallback(() => {
    setBild(null);
    setBildFehler(null);
    setFehler(null);
  }, []);

  const bildGewaehlt = useCallback(async (datei: File | null): Promise<void> => {
    if (!datei) return;
    setBild(null);
    setBildFehler(null);
    setFehler(null);
    setBildArbeitet(true);
    let ergebnis: string | null = null;
    let problem: string | null = null;
    try {
      ergebnis = await bereiteUnterschriftBildAuf(datei);
    } catch (err) {
      problem = err instanceof Error && err.message ? err.message : describeError(err);
    }
    if (!montiert.current) return;
    setBildArbeitet(false);
    if (ergebnis) setBild({ daten: ergebnis, art: 'bild' });
    else setBildFehler(problem || 'Das Bild ließ sich nicht verwenden.');
  }, []);

  // ── Wege am Rechner ─────────────────────────────────────────────────────

  const qrOeffnen = useCallback(async (): Promise<void> => {
    setWeg('qr');
    setBild(null);
    setFehler(null);
    setQrFehler(null);
    setAbfrageZeitUm(false);
    setAbfrageStart(Date.now());
    let url: string | null = null;
    let problem: string | null = null;
    try {
      // Derselbe Link: wer die Seite am Rechner offen hat, hat den Token schon.
      url = await QRCode.toDataURL(window.location.href, { width: 320, margin: 2 });
    } catch (err) {
      problem = `Der QR-Code ließ sich nicht erzeugen: ${describeError(err)}`;
    }
    if (!montiert.current) return;
    setQrBild(url);
    setQrFehler(problem);
  }, []);

  const wegWaehlen = useCallback((neu: RechnerWeg) => {
    if (neu === 'qr') {
      void qrOeffnen();
      return;
    }
    setWeg(neu);
    setAbfrageStart(null);
    setBild(null);
    setBildFehler(null);
    setFehler(null);
    if (neu === 'maus') setPadOffen(true);
  }, [qrOeffnen]);

  const wegZurueck = useCallback(() => {
    setWeg(null);
    setAbfrageStart(null);
    setAbfrageZeitUm(false);
    setBild(null);
    setBildFehler(null);
    setFehler(null);
  }, []);

  const qrAktiv = weg === 'qr' && seitenZustand(lade) === 'offen';

  /** Einmal nachsehen, ob am Handy unterschrieben wurde. */
  const pruefeEinmal = useCallback(async (): Promise<void> => {
    const e = await ladeAbsichern(token);
    if (!montiert.current) return;
    if (abfrageUebernehmen(e)) setLade(e);
  }, [token]);

  // Abfrage-Uhr: alle 5 s, nur bei sichtbarem Tab, höchstens 15 min.
  useEffect(() => {
    if (!qrAktiv || abfrageStart === null) return;
    let aus = false;
    let laeuft = false;
    let uhr = 0;
    const schritt = async (): Promise<void> => {
      if (aus || laeuft) return;
      const s = abfrageSchritt({
        aktiv: true,
        sichtbar: document.visibilityState === 'visible',
        startMs: abfrageStart,
        jetztMs: Date.now(),
      });
      if (s === 'zeit_um') {
        aus = true;
        window.clearInterval(uhr);
        setAbfrageZeitUm(true);
        return;
      }
      if (s !== 'abfragen') return;
      laeuft = true;
      await pruefeEinmal();
      laeuft = false;
    };
    uhr = window.setInterval(() => { void schritt(); }, ABFRAGE_TAKT_MS);
    // Zurück im Vordergrund: sofort nachsehen statt bis zu 5 s zu warten.
    const beiSichtwechsel = () => {
      if (document.visibilityState === 'visible') void schritt();
    };
    document.addEventListener('visibilitychange', beiSichtwechsel);
    return () => {
      aus = true;
      window.clearInterval(uhr);
      document.removeEventListener('visibilitychange', beiSichtwechsel);
    };
  }, [abfrageStart, pruefeEinmal, qrAktiv]);

  const erneutPruefen = useCallback(() => {
    setAbfrageZeitUm(false);
    setAbfrageStart(Date.now());
    void pruefeEinmal();
  }, [pruefeEinmal]);

  // ── Absenden ────────────────────────────────────────────────────────────

  const unterschreiben = useCallback(async (): Promise<void> => {
    if (sendetRef.current || seitenZustand(lade) !== 'offen' || !bild) return;
    // Unmittelbar vor dem Absenden den aktuellen Tag nehmen (Tab über
    // Mitternacht offen): die Vorbelegung folgt ihm, ein selbst geändertes
    // Datum bleibt, solange es gilt.
    const tag = berlinIsoTag(new Date());
    const g = datumGrenzen(fassungVom, tag);
    const feld = datumFeldFuer(datumFeld, g);
    setHeute(tag);
    if (!gleichesDatumFeld(feld, datumFeld)) setDatumFeld(feld);
    // Ein selbst gewähltes Datum, das nicht mehr gilt, nie still ersetzt absenden.
    if (datumSelbstGeaendert(datumFeld) && feld.wert !== datumFeld.wert) {
      setFehler('Das gewählte Datum gilt nicht mehr und wurde auf heute gesetzt. Bitte prüfen und erneut absenden.');
      return;
    }
    if (!kannUnterschreiben({ gelesen, ...name, funktion, datum: feld.wert, grenzen: g, bild: bild.daten })) return;
    sendetRef.current = true;
    setSendet(true);
    setFehler(null);
    let antwort: VorankRouteAntwort | null = null;
    try {
      antwort = await sendeVorankAnfrage({
        aktion: 'unterschreiben',
        token,
        name: vollerName(name),
        funktion: bereinigeName(funktion),
        bild: bild.daten,
        art: bild.art,
        datum: feld.wert,
      });
    } catch {
      antwort = null;
    }
    sendetRef.current = false;
    if (!montiert.current) return;
    setSendet(false);
    const folge = antwort ? folgeNachSenden('unterschreiben', antwort) : NETZ_FOLGE;
    setFehler(folge.fehler);
    folgeAnwenden(folge, { bild, datum: feld.wert });
  }, [bild, datumFeld, fassungVom, folgeAnwenden, funktion, gelesen, lade, name, token]);

  const korrekturSenden = useCallback(async (): Promise<void> => {
    if (sendetRef.current || seitenZustand(lade) !== 'offen') return;
    if (korrekturHinweis({ ...korrekturName, text: korrekturText })) return;
    sendetRef.current = true;
    setKorrekturSendet(true);
    setKorrekturFehler(null);
    let antwort: VorankRouteAntwort | null = null;
    try {
      antwort = await sendeVorankAnfrage({
        aktion: 'korrektur',
        token,
        name: vollerName(korrekturName),
        text: bereinigeText(korrekturText),
      });
    } catch {
      antwort = null;
    }
    sendetRef.current = false;
    if (!montiert.current) return;
    setKorrekturSendet(false);
    const folge = antwort ? folgeNachSenden('korrektur', antwort) : NETZ_FOLGE;
    setKorrekturFehler(folge.fehler);
    if (folge.korrekturGemeldet) {
      setKorrekturGemeldet(true);
      setKorrekturOffen(false);
      setKorrekturText('');
    }
    folgeAnwenden(folge, null);
  }, [folgeAnwenden, korrekturName, korrekturText, lade, token]);

  // ── PDF ─────────────────────────────────────────────────────────────────

  const daten = ladeDaten(lade);

  /**
   * „PDF ansehen“: aus dem Inhalt der Fassung im Browser erzeugt. Synchron im
   * Klick, damit der Browser das neue Fenster nicht blockiert; danach stehen
   * trotzdem „öffnen“ und „herunterladen“ als Links da.
   */
  const pdfAnsehen = useCallback(() => {
    if (!daten) return;
    if (pdf.status === 'bereit') {
      window.open(pdf.url, '_blank', 'noopener');
      return;
    }
    let url: string;
    try {
      url = pdfAdresse(daten.inhalt, null);
    } catch (err) {
      setPdf({ status: 'fehler', text: `Das PDF ließ sich nicht erzeugen: ${describeError(err)}` });
      return;
    }
    pdfAdressen.current.push(url);
    setPdf({ status: 'bereit', url });
    window.open(url, '_blank', 'noopener');
  }, [daten, pdf]);

  /** „Unterschriebene Vorankündigung herunterladen“: Fassung + Unterschrift aus der Antwort. */
  const signiertHerunterladen = useCallback(() => {
    if (!daten) return;
    const dateiname = vorankPdfDateiname(daten, true);
    if (pdfSigniert.status === 'bereit') {
      herunterladen(pdfSigniert.url, dateiname);
      return;
    }
    const unterschrift = pdfUnterschriftAus(daten, token);
    if (!unterschrift) return;
    let url: string;
    try {
      url = pdfAdresse(daten.inhalt, unterschrift);
    } catch (err) {
      setPdfSigniert({ status: 'fehler', text: `Das PDF ließ sich nicht erzeugen: ${describeError(err)}` });
      return;
    }
    pdfAdressen.current.push(url);
    setPdfSigniert({ status: 'bereit', url });
    herunterladen(url, dateiname);
  }, [daten, pdfSigniert, token]);

  return {
    token,
    lade,
    daten,
    zustand: seitenZustand(lade),
    geraet,
    erneutLaden,
    // Formular
    gelesen,
    setGelesen,
    vorname: name.vorname,
    setVorname: (wert: string) => setName((n) => ({ ...n, vorname: wert })),
    nachname: name.nachname,
    setNachname: (wert: string) => setName((n) => ({ ...n, nachname: wert })),
    datum: datumFeld.wert,
    setDatum: (wert: string) => setDatumFeld((f) => ({ ...f, wert })),
    /** Fokus auf das Datumsfeld: `max` mit dem aktuellen Tag. */
    datumFokus: tagNeuNehmen,
    grenzen,
    funktion,
    setFunktion,
    bild,
    padOffen,
    padOeffnen: () => setPadOffen(true),
    padSchliessen: () => setPadOffen(false),
    unterschriftUebernehmen,
    unterschriftLoeschen,
    bildGewaehlt,
    bildArbeitet,
    bildFehler,
    sendet,
    fehler,
    danke,
    unterschreiben,
    // Rechner
    weg,
    wegWaehlen,
    wegZurueck,
    qrBild,
    qrFehler,
    abfrageZeitUm,
    erneutPruefen,
    // Korrektur
    korrekturOffen,
    korrekturOeffnen: () => {
      setKorrekturOffen(true);
      setKorrekturFehler(null);
    },
    korrekturSchliessen: () => setKorrekturOffen(false),
    korrekturVorname: korrekturName.vorname,
    setKorrekturVorname: (wert: string) => setKorrekturName((n) => ({ ...n, vorname: wert })),
    korrekturNachname: korrekturName.nachname,
    setKorrekturNachname: (wert: string) => setKorrekturName((n) => ({ ...n, nachname: wert })),
    korrekturText,
    setKorrekturText,
    korrekturSendet,
    korrekturFehler,
    korrekturGemeldet,
    korrekturSenden,
    // PDF
    pdf,
    pdfAnsehen,
    pdfSigniert,
    signiertHerunterladen,
  };
}

export type VorankUnterschriftSteuerung = ReturnType<typeof useVorankUnterschrift>;
